import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Chip,
  Popover,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useEffect, useState } from "react";
import { fetchData } from "../../common/services/ApiClient";
import { sortObjValue, toTitleCase } from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import SearchPills from "../../containers/SearchPills";
import {
  KeyValue,
  Locations,
  Query,
  SearchPillData,
  Sort,
  StateRow,
  TableHeader,
} from "../../interfaces";
import { selectedStatesAlpha } from "../../refdata/FilterOptions";
import ModalDialog from "../ModalDialog";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import OfficeMapDisplay from "../OfficeMapDisplay";
import ShowHideButton from "../ShowHideButton";
import ResultsViewToggle from "../FusionCharts/ResultsViewToggle";
import TableChartIcon from "@mui/icons-material/TableChart";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import USAMap from "../../containers/FIBUSAMap";
import FibFinancialsChart from "../FusionCharts/FIBFinancialsChart";

type Report = "branch-office-locations" | "branch-office-count";

interface DropDownOptions {
  label: string;
  value: Report;
}

const dropDownOptions: DropDownOptions[] = [
  { label: "Branch Office Locations", value: "branch-office-locations" },
  {
    label: "Branch Office Count - Annual, Ten Year",
    value: "branch-office-count",
  },
];

const PREFIX = "BankFindLocations";

const fields = [
  "ACQDATE",
  "ADDRESS",
  "ADDRESS2",
  "CITY",
  "COUNTY",
  "ESTYMD",
  "LATITUDE",
  "LONGITUDE",
  "MAINOFF",
  "NAME",
  "OFFNAME",
  "OFFNUM",
  "SERVTYPE",
  "SERVTYPE_DESC",
  "STALP",
  "UNINUM",
  "ZIP",
];

const classes = {
  locationPills: `${PREFIX}-locationPills`,
  h4: `${PREFIX}-h4`,
  openCloseAll: `${PREFIX}-openCloseAll`,
  tableRowStyles: `${PREFIX}-tableRowStyles`,
  totalsRow: `${PREFIX}-totalsRow`,
  chip: `${PREFIX}-chip`,
  rowIndent: `${PREFIX}-rowIndent`,
  rowIndentCounties: `${PREFIX}-rowIndentCounties`,
  rowIndentInstitutions: `${PREFIX}-rowIndentInstitutions`,
  rowStyles: `${PREFIX}-rowStyles`,
  noResults: `${PREFIX}-noResults`,
  tableRow: `${PREFIX}-tableRow`,
  mainOfficeRow: `${PREFIX}-mainOfficeRow`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.locationPills}`]: {
    "& .MuiCollapse-root": {
      // marginTop: -25,
      marginBottom: 10,
    },
  },

  [`& .${classes.h4}`]: {
    color: "#1A4480",
    padding: "0",
    fontSize: "1rem",
    fontWeight: "bold",
    lineHeight: "1.5rem",
    paddingBottom: "1rem",
  },

  [`& .${classes.openCloseAll}`]: {
    "minWidth": 80,
    "margin": 0,
    "color": "#ffffff",
    "fontWeight": 600,
    "backgroundColor": "#426cac",
    "&:hover, &.Mui-focusVisible": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.secondary.main,
    },
  },

  [`& .${classes.tableRowStyles}`]: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body":
      {
        cursor: "pointer",
        filter: "brightness(.9)",
      },
  },

  [`& .${classes.totalsRow}`]: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#194480",
      fontWeight: "bold",
    },
  },

  [`& .${classes.chip}`]: {
    "cursor": "pointer",
    "backgroundColor": "transparent",
    "color": theme.palette.primary.main,
    "fontSize": 14,
    "float": "left",
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
    },
  },

  [`& .${classes.rowIndent}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
    },
  },

  [`& .${classes.rowIndentCounties}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 30,
    },
  },

  [`& .${classes.rowIndentInstitutions}`]: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 70,
      color: "blue",
      textDecoration: "underline",
    },
  },

  [`& .${classes.rowStyles}`]: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.primary.main,
      fontWeight: "bold",
    },
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body":
      {
        cursor: "pointer",
        filter: "brightness(.93)",
      },
  },

  [`& .${classes.noResults}`]: {
    "&.MuiTableRow-root td": {
      height: 300,
      paddingTop: "1rem",
      fontSize: "1rem",
      display: "block",
      position: "absolute",
    },
  },

  [`& .${classes.tableRow}`]: {
    "cursor": "pointer",
    "&.MuiTableRow-root:hover, &.MuiTableRow-root:focus-visible": {
      background: "#BEDBDB",
    },
  },

  [`& .${classes.mainOfficeRow}`]: {
    "cursor": "pointer",
    "&.MuiTableRow-root:hover, &.MuiTableRow-root:focus-visible": {
      background: "#BEDBDB",
    },
    "& td.MuiTableCell-root.MuiTableCell-body": {
      fontWeight: "bold",
    },
  },
}));

interface Props {
  cert: string;
  appState: Query;
  locationsSearch?: string;
  updateFilter: (field: string, value: string) => void;
}

interface StateListProps {
  open: string[];
  setOpen: React.Dispatch<React.SetStateAction<string[]>>;
  countyData: KeyValue;
  stateData: StateRow[];
  total: number;
  openCounties: string[];
  setOpenCounties: React.Dispatch<React.SetStateAction<string[]>>;
}

interface CountyListProps {
  countyData: KeyValue;
  openCounties: string[];
  setOpenCounties: React.Dispatch<React.SetStateAction<string[]>>;
}

const BankFindLocationsInstituteList = ({ locations }: any) => {
  const [openDialog, setOpenDialog] = React.useState(false);

  const [mapData, setMapData] = React.useState<Locations>({
    ACQDATE: "",
    ADDRESS: "",
    ADDRESS2: "",
    CITY: "",
    COUNTY: "",
    ESTYMD: "",
    LATITUDE: 0,
    LONGITUDE: 0,
    MAINOFF: 0,
    NAME: "",
    OFFNAME: "",
    OFFNUM: "",
    SERVTYPE: "",
    SERVTYPE_DESC: "",
    STALP: "",
    UNINUM: "",
    ZIP: "",
  });
  const handleMouseClick = (data: KeyValue, event: KeyValue) => {
    setMapData({
      ACQDATE: data.ACQDATE,
      ADDRESS: data.ADDRESS,
      ADDRESS2: data.ADDRESS2,
      CITY: data.CITY,
      COUNTY: data.COUNTY,
      ESTYMD: data.ESTYMD,
      LATITUDE: data.LATITUDE,
      LONGITUDE: data.LONGITUDE,
      MAINOFF: data.MAINOFF,
      NAME: data.NAME,
      OFFNAME: data.OFFNAME,
      OFFNUM: data.OFFNUM,
      SERVTYPE: data.SERVTYPE,
      SERVTYPE_DESC: data.SERVTYPE_DESC,
      STALP: data.STALP,
      UNINUM: data.UNINUM,
      ZIP: data.ZIP,
    });
    setOpenDialog(true);
  };

  const locationsRows = sortObjValue(locations, "CITY").map(
    (data: KeyValue, index: number) => {
      const mainOffice = data.OFFNUM === 0;
      return (
        <TableRow
          className={mainOffice ? classes.mainOfficeRow : classes.tableRow}
          onClick={(event) => handleMouseClick(data, event)}
          key={index}
          sx={{ backgroundColor: index % 2 === 1 ? "#E8ECF2" : null }}
          id={`${data.UNINUM}-${index}`}
        >
          <TableCell className={classes.rowIndentInstitutions}>
            {data.ADDRESS}
            {data.ADDRESS2 ? `, ${data.ADDRESS2}` : ""}
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell>{data.CITY}</TableCell>
          <TableCell>{data.ZIP}</TableCell>
          <TableCell>
            <div id="uninum-value">{data.UNINUM}</div>
          </TableCell>
          <TableCell>{mainOffice ? "Main Office" : data.OFFNUM}</TableCell>
          <TableCell>{data.SERVTYPE_DESC}</TableCell>
          <TableCell>{data.ESTYMD}</TableCell>
          <TableCell>{data.ACQDATE}</TableCell>
        </TableRow>
      );
    },
  );

  return (
    <>
      {locationsRows}
      <ModalDialog
        id="branch-office-modal"
        title={toTitleCase(mapData.OFFNAME)}
        subtitle={`Branch Number: ${
          mapData.MAINOFF === 1 ? "Main Office" : mapData.OFFNUM
        }`}
        display={openDialog}
        setDisplay={setOpenDialog}
      >
        <div style={{ paddingBottom: 16 }}>
          <OfficeMapDisplay data={mapData} useDefaultAddress showDateAcquired />
        </div>
      </ModalDialog>
    </>
  );
};

const BankFindLocationsCountyList = ({
  countyData,
  openCounties,
  setOpenCounties,
}: CountyListProps) => {
  let sortedCounties = Object.keys(countyData).map((c) => ({
    ...countyData[c],
    name: c,
  }));
  sortedCounties = sortObjValue(sortedCounties, "name");

  const countyList = countyData ? (
    sortedCounties.map((data) => {
      const countyName = data.name;
      const countyTotal = data.count;
      const institutions = data.locations;

      const expandCollapse = (e: any) => {
        const otherClicks = ["Open All", "Close All"];
        if (!otherClicks.includes(e.target.innerText)) {
          !openCounties.includes(countyName)
            ? setOpenCounties([...openCounties, countyName])
            : setOpenCounties(
                openCounties.filter((county) => county !== countyName),
              );
        }
      };
      const expandCollapseProps = {
        onKeyDown: (e: any) => {
          if (e.key === "Enter") {
            expandCollapse(e);
          }
        },
        onClick: (e: any) => expandCollapse(e),
      };
      return (
        <React.Fragment key={`${countyName}_${countyTotal}`}>
          <TableRow
            id={`${countyName
              .toLowerCase()
              .replace(/ /g, "")}_expand_${countyTotal}`}
            className={classes.rowStyles}
            tabIndex={0}
            {...expandCollapseProps}
          >
            <TableCell className={classes.rowIndentCounties}>
              {openCounties.includes(countyName) ? (
                <Chip
                  className={classes.chip}
                  icon={<ExpandMoreIcon />}
                  label={countyName}
                />
              ) : (
                <Chip
                  className={classes.chip}
                  icon={<ChevronRightIcon />}
                  label={countyName}
                />
              )}
            </TableCell>
            <TableCell></TableCell>
            <TableCell>{countyTotal?.toLocaleString()}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
          {openCounties.includes(countyName) && (
            <BankFindLocationsInstituteList locations={institutions} />
          )}
        </React.Fragment>
      );
    })
  ) : (
    <></>
  );

  return <>{countyList}</>;
};

const BankFindLocationsStateList = ({
  open,
  setOpen,
  countyData,
  stateData,
  total,
  openCounties,
  setOpenCounties,
}: StateListProps) => {
  const stateRows = stateData.map((data, int) => {
    const expandCollapse = (e: any) => {
      const otherClicks = ["Open All", "Close All"];
      if (!otherClicks.includes(e.target.innerText)) {
        !open.includes(data.STALP)
          ? setOpen([...open, data.STALP])
          : setOpen(open.filter((state: any) => state !== data.STALP));
      }
    };

    const expandCollapseProps = {
      onKeyDown: (e: any) => {
        if (e.key === "Enter") {
          expandCollapse(e);
        }
      },
      onClick: (e: any) => expandCollapse(e),
    };
    const allCounties = Object.keys(countyData[data.STALP]);

    const handleOpenCloseAll = () => {
      if (allCounties.length === openCounties.length) {
        setOpenCounties([]);
      } else {
        setOpenCounties(allCounties);
      }
    };
    return (
      data?.STALP && (
        <React.Fragment key={`${data.STALP}_${int}`}>
          <TableRow
            id={`${data.STALP.toLowerCase().replace(/ /g, "")}_expand_${int}`}
            className={classes.tableRowStyles}
            tabIndex={0}
            {...expandCollapseProps}
          >
            <TableCell className={classes.rowIndent}>
              {open.includes(data.STALP) ? (
                <Chip
                  className={classes.chip}
                  icon={<ExpandMoreIcon />}
                  label={`${
                    selectedStatesAlpha.find(
                      (state: KeyValue) => state.value === data.STALP,
                    )?.label ?? "Foreign Branch Offices"
                  }`}
                />
              ) : (
                <Chip
                  className={classes.chip}
                  icon={<ChevronRightIcon />}
                  label={`${
                    selectedStatesAlpha.find(
                      (state: KeyValue) => state.value === data.STALP,
                    )?.label ?? "Foreign Branch Offices"
                  }`}
                />
              )}
            </TableCell>
            <TableCell style={{ padding: 0 }}>
              {open.includes(data.STALP) && (
                <Button
                  id={
                    allCounties.length === openCounties.length
                      ? `closeAllCounties_${data.STALP}`
                      : `openAllCounties_${data.STALP}`
                  }
                  className={classes.openCloseAll}
                  onClick={handleOpenCloseAll}
                >
                  {allCounties.length === openCounties.length
                    ? "Close All"
                    : "Open All"}
                </Button>
              )}
            </TableCell>
            <TableCell>{data.count?.toLocaleString()}</TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </TableRow>
          {open.includes(data.STALP) &&
            (data.STALP === "Foreign Branch Offices" ? (
              <BankFindLocationsInstituteList
                locations={
                  countyData[data.STALP]?.["Foreign Branch Offices"]
                    ?.locations || []
                }
              />
            ) : (
              <BankFindLocationsCountyList
                countyData={countyData[data.STALP]}
                openCounties={openCounties}
                setOpenCounties={setOpenCounties}
              />
            ))}
        </React.Fragment>
      )
    );
  });
  const totalsRow = (
    <TableRow className={classes.totalsRow} tabIndex={0}>
      <TableCell className={classes.rowIndent}>
        <Typography
          sx={{
            color: "#FFFFFF",
            fontWeight: "bold",
          }}
        >
          Total Offices
        </Typography>
      </TableCell>
      <TableCell></TableCell>
      <TableCell>
        <Typography
          sx={{
            color: "#FFFFFF",
            fontWeight: "bold",
          }}
        >
          {total?.toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
    </TableRow>
  );
  return (
    <>
      {stateRows.length > 0 ? (
        <>
          {stateRows}
          {totalsRow}
        </>
      ) : (
        <TableRow
          className={classes.noResults}
          style={{ backgroundColor: "#fff" }}
        >
          <TableCell>
            "Double-check your search criteria for errors, or try your search
            again limiting or removing location-based criteria."
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const getCountHeaders = (risdate: string): TableHeader[][] => {
  const risYear = Number(risdate.slice(0, 4));
  const years: string[] = [String(risYear)];
  for (let i = 1; i < 10; i++) {
    years.push(String(risYear - i));
  }

  return [
    [
      {
        title: "Description",
        apiField: "0",
        colSpan: 3,
        styles: {
          fontSize: 16,
          fontWeight: "bold",
          width: 300,
          textAlign: "left",
        },
      },
      ...years.reverse().map((yr) => ({
        title: yr,
        apiField: yr,
        colSpan: 1,
        styles: {
          fontSize: 16,
          fontWeight: "bold",
          width: 100,
        },
      })),
    ],
  ];
};

const BankFindLocationsCountTable = ({
  data,
}: {
  data: { OFFFOR: []; OFFDOM: []; totals: []; definitions: string[] };
}) => {
  const DescriptionCell = (str: string, desc: string, def: string) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    return (
      <TableCell
        colSpan={3}
        tabIndex={0}
        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        }}
        onMouseLeave={() => setAnchorEl(null)}
        onFocus={(event: React.FocusEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        }}
        onBlur={() => setAnchorEl(null)}
        sx={{
          "&:hover, &:focus-visible": {
            backgroundColor: "#d9e8f7",
          },
        }}
        id={str}
      >
        <Box display="flex">
          <Typography
            sx={{
              color: "#0000EE",
              textDecoration: "underline",
              fontWeight: "bold",
              textAlign: "left",
            }}
          >
            {str}
          </Typography>
          <Typography color="primary.main" ml={"0.5rem"}>
            - {desc}
          </Typography>
        </Box>
        <Popover
          id="mouse-over-popover"
          sx={{
            pointerEvents: "none",
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{ paper: { style: { maxWidth: "750px" } } }}
          onClose={() => setAnchorEl(null)}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
        >
          <Typography style={{ padding: "1rem 1rem 0" }} variant="h3">
            {str}
          </Typography>
          <Typography style={{ padding: "0 1rem 1rem" }} variant="body1">
            {def}
          </Typography>
        </Popover>
      </TableCell>
    );
  };
  return (
    <>
      <TableRow>
        {DescriptionCell(
          "OFFDOM",
          "Number of Domestic Offices",
          data.definitions[0],
        )}
        {data.OFFDOM.map((count, i) => (
          <TableCell colSpan={1} key={`OFFDOM${i}`}>
            {count}
          </TableCell>
        ))}
      </TableRow>

      <TableRow>
        {DescriptionCell(
          "OFFFOR",
          "Number of Foreign Offices",
          data.definitions[1],
        )}
        {data.OFFFOR.map((count, i) => (
          <TableCell colSpan={1} key={`OFFFOR${i}`}>
            {count}
          </TableCell>
        ))}
      </TableRow>
      <TableRow sx={{ backgroundColor: "#d9e8f7" }}>
        <TableCell colSpan={3} sx={{ justifyItems: "right" }}>
          <Typography color="primary.main" fontWeight="bold">
            Total
          </Typography>
        </TableCell>
        {data.totals.map((count, i) => (
          <TableCell colSpan={1} key={`totals${i}`}>
            {count}
          </TableCell>
        ))}
      </TableRow>
    </>
  );
};

export default function BankFindLocations({
  cert,
  locationsSearch,
  appState,
  updateFilter,
}: Props) {
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [locationsData, setLocationsData] = useState<Locations[]>([]);
  const [countData, setCountData] = useState<{
    OFFFOR: [];
    OFFDOM: [];
    totals: [];
    definitions: [];
  }>({
    OFFFOR: [],
    OFFDOM: [],
    totals: [],
    definitions: [],
  });
  const [risdate, setRisdate] = useState<string>("");
  const [masterTotal, setMasterTotal] = useState<number>(0);
  const [totalOfficeCount, setTotalOfficeCount] = useState<number>(0);
  const [sort, setSort] = useState<Sort>({
    sortField: "OFFNUM",
    sortOrder: "ASC",
  });
  const [report, setReport] = useState<Report>("branch-office-locations");
  const [statesOpen, setStatesOpen] = useState<string[]>([]);
  const [openCounties, setOpenCounties] = React.useState<string[]>([]);
  const [stateRows, setStateRows] = useState<any[]>([]);
  const [countyRows, setCountyRows] = useState<KeyValue>({});
  const [dataView, setDataView] = useState<"table" | "map">("table");

  const [chartCategories, setChartCategories] = useState<
    {
      category: {
        label: string;
      }[];
    }[]
  >([]);
  const [chartData, setChartData] = useState<
    {
      seriesName: string;
      data: {
        value: string;
      }[];
    }[]
  >([]);

  const chartInstanceRef = React.useRef<any>(null);

  const allStates = stateRows
    .filter((curr) => curr.STALP !== "")
    .map((curr) => curr.STALP);

  const handleOpenCloseAll = () => {
    if (allStates.length === statesOpen.length) {
      setStatesOpen([]);
    } else {
      setStatesOpen(allStates);
    }
  };

  const openCloseButton = (
    <Button
      id={
        allStates.length === statesOpen.length
          ? "closeAllStates"
          : "openAllStates"
      }
      className={classes.openCloseAll}
      onClick={() => {
        handleOpenCloseAll();
      }}
    >
      {allStates.length === statesOpen.length ? "Close All" : "Open All"}
    </Button>
  );

  const HeadersMetaData: KeyValue[] = [
    { title: "State/County/Street Address", apiField: "STALP" },
    {
      title: openCloseButton,
      apiField: "",
    },
    { title: "Matching Offices", apiField: "count" },
    { title: "City (USPS)", apiField: "" },
    { title: "ZIP", apiField: "" },
    { title: "UNINUM", apiField: "" },
    { title: "Office Number", apiField: "" },
    { title: "Service Type", apiField: "" },
    { title: "Established", apiField: "" },
    { title: "Acquired", apiField: "" },
  ];

  const LocationHeaders: TableHeader[][] = [
    HeadersMetaData.map((value: KeyValue) => ({
      title: value.title,
      styles: { width: "full-width" },
      colSpan: 1,
      apiField: value.apiField !== "address" ? value.apiField : undefined,
    })),
  ];
  const counts = locationsData.reduce((acc: Record<string, number>, branch) => {
    const state =
      appState.filters.stalpbr === ""
        ? branch.STALP === ""
          ? "Foreign Branch Offices"
          : branch.STALP
        : branch.COUNTY;
    acc[state] = (acc[state] || 0) + 1;
    return acc;
  }, {});

  const mapData = Object.entries(counts)
    .map(([id, value]) => ({ id, value }))
    .sort((a, b) => a.id.localeCompare(b.id));

  useEffect(() => {
    if (stateRows.length > 0) {
      const stateName = stateRows[0].STNAME;
      const statePill = appState.filters.stalp !== "" && {
        value: stateName,
        formKey: "stalp",
      };
      const cityPill = appState.search.city !== "" && {
        value: appState.search.city,
        formKey: "city",
        search: true,
      };
      const zipPill = appState.filters.zip !== "" && {
        value: appState.filters.zip,
        formKey: "zip",
      };
      let dataArray: SearchPillData[] = [];
      statePill && dataArray.push(statePill);
      cityPill && dataArray.push(cityPill);
      zipPill && dataArray.push(zipPill);
    }
  }, [
    stateRows,
    totalOfficeCount,
    appState.filters.stalp,
    appState.filters.zip,
    appState.search.city,
    locationsData.length,
  ]);

  useEffect(() => {
    if (locationsData.length > 0) {
      let filteredLocations = [...locationsData];
      if (appState.filters.county !== "") {
        filteredLocations = filteredLocations.filter(
          (loc) => loc.COUNTY === appState.filters.county,
        );
        setTotalOfficeCount(filteredLocations.length);
      }
      let tempStateData = Object.values(
        filteredLocations.reduce((acc: any, bank: any) => {
          acc[bank.STALP] = acc[bank.STALP] || [];
          acc[bank.STALP].push(bank);
          return acc;
        }, {}),
      );
      const stateRow: StateRow[] = tempStateData.map((state: any) => ({
        STALP: state[0].STALP || "Foreign Branch Offices",
        STNAME: state[0].STNAME,
        count: state.length,
      }));

      const sorted = stateRow.sort((a, b) => {
        if (a.STALP === "Foreign Branch Offices") return 1;
        if (b.STALP === "Foreign Branch Offices") return -1;
        return a.STALP.localeCompare(b.STALP);
      });

      setStateRows(sorted);

      let tempCountys: any = filteredLocations.reduce((acc: any, location) => {
        let { COUNTY, STALP } = location;

        STALP = STALP || "Foreign Branch Offices";
        COUNTY = COUNTY || "Foreign Branch Offices";

        if (acc[STALP]) {
          let tempState = { ...acc[STALP] };
          if (Object.hasOwn(tempState, COUNTY)) {
            tempState[COUNTY].count += 1;
            tempState[COUNTY].locations = [
              ...tempState[COUNTY].locations,
              location,
            ];
          } else {
            tempState[COUNTY] = { count: 1, locations: [location] };
          }
          acc[STALP] = tempState;
        } else {
          acc[STALP] = {};
          acc[STALP][COUNTY] = { count: 1, locations: [location] };
        }
        return acc;
      }, {});
      setCountyRows(tempCountys);
    }
  }, [
    appState.filters.stalp,
    appState.search.city,
    appState.filters.zip,
    locationsData,
    totalOfficeCount,
    appState.filters.county,
    appState.filters.stalpbr,
  ]);

  // get locations count total and risdate
  useEffect(() => {
    if (initialLoad) {
      if (appState.filters.stalp !== appState.filters.stalpbr) {
        updateFilter("stalpbr", appState.filters.stalp || "");
      }
      if (appState.filters.countyInst !== appState.filters.county) {
        updateFilter("county", appState.filters.countyInst || "");
      }
      (async () => {
        Promise.all([
          fetchData(`${ApiBaseUrl()}/locations?filters=CERT:${cert}&limit=0`),
          fetchData(
            `${ApiBaseUrl()}/financials?filters=CERT:${cert}&limit=1&fields=RISDATE&sort_by=RISDATE&sort_order=DESC`,
          ),
        ]).then((res) => {
          setMasterTotal(res[0].totals.count);
          setRisdate(res[1].data[0].RISDATE);
        });
      })();
      setInitialLoad(false);
    }
  }, [
    appState.filters.county,
    appState.filters.countyInst,
    appState.filters.stalp,
    appState.filters.stalpbr,
    cert,
    initialLoad,
    updateFilter,
  ]);
  useEffect(() => {
    let isMounted = true;
    if (report === "branch-office-locations") {
      const filters = `${appState.filters.stalpbr ? ` AND STALP:"${appState.filters.stalpbr === "Foreign Branch Offices" ? "" : appState.filters.stalpbr}"` : ""}`;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/locations?filters=CERT:${cert}${filters}&limit=10000&fields=${fields.join(",")}`,
        )
          .then((response) => {
            if (isMounted) {
              setTotalOfficeCount(response.totals.count);
              setLocationsData(response.data);
              setStatesOpen(
                appState.filters.stalpbr === ""
                  ? []
                  : [appState.filters.stalpbr],
              );
              setOpenCounties(
                appState.filters.county === "" ? [] : [appState.filters.county],
              );
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    } else if (report === "branch-office-count") {
      (async () => {
        const year = parseInt(risdate.slice(0, 4), 10);
        const result = [risdate];
        for (let i = 1; i < 10; i++) {
          result.push(`${year - i}1231`);
        }
        const risdates = ` AND RISDATE:(${result.join(" OR ")})`;
        await Promise.all([
          fetchData(
            `${ApiBaseUrl()}/financials?filters=CERT:${cert}${risdates}&limit=10&fields=OFFDOM,OFFFOR,RISDATE&sort_by=RISDATE&sort_order=ASC`,
          ),
          fetchData(
            `${ApiBaseUrl()}/definitions?filters=VARIABLE:(OFFDOM OR OFFFOR)&sort_by=VARIABLE&sort_order=ASC`,
          ),
        ])
          .then((response) => {
            const offdomData = response[0].data.map((item: any) => item.OFFDOM);
            const offforData = response[0].data.map((item: any) => item.OFFFOR);
            while (offdomData.length < 10) {
              offdomData.unshift(0);
            }
            while (offforData.length < 10) {
              offforData.unshift(0);
            }
            const totals = offdomData.map(
              (val: string, i: string) => val + offforData[i],
            );
            const definitions = response[1].data.map(
              (variable: any) => variable.DEFINITION,
            );
            setCountData({
              OFFDOM: offdomData,
              OFFFOR: offforData,
              totals: totals,
              definitions: definitions,
            });
            const categories = result.reverse().map((year) => ({
              label: year.slice(0, 4),
            }));
            setChartCategories([{ category: categories }]);
            const newChartData = [];
            newChartData.push({
              data: offdomData.map((data: any) => ({ value: data })),
              seriesName: "Domestic Branch Offices",
            });
            newChartData.push({
              data: offforData.map((data: any) => ({ value: data })),
              seriesName: "Foreign Branch Offices",
            });
            newChartData.push({
              data: totals.map((data: any) => ({ value: data })),
              seriesName: "Total Branch Offices",
              renderAs: "line",
              showValues: "1",
              valuePosition: "ABOVE",
            });
            setChartData(newChartData);
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
  }, [
    appState.filters.stalp,
    appState.filters.county,
    appState.filters.stalpbr,
    cert,
    locationsSearch,
    report,
    risdate,
    countData.OFFDOM.length,
  ]);

  function sortHandler(tableHeader: TableHeader) {
    if (tableHeader.apiField) {
      const sortDirection = sort.sortOrder === "ASC" ? "DESC" : "ASC";
      setSort({ sortField: tableHeader.apiField, sortOrder: sortDirection });
      if (tableHeader.apiField === "count") {
        const sorted =
          sortDirection === "ASC"
            ? [...stateRows].sort((a, b) => a.count - b.count)
            : [...stateRows].sort((a, b) => b.count - a.count);
        setStateRows(sorted);
      }
      if (tableHeader.apiField === "STALP") {
        const sorted =
          sortDirection === "ASC"
            ? [...stateRows].sort((a, b) => a.STALP.localeCompare(b.STALP))
            : [...stateRows].sort((a, b) => b.STALP.localeCompare(a.STALP));
        setStateRows(sorted);
      }
    }
  }
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "State",
      value: `${appState.filters.stalpbr}`,
      search: false,
      static: false,
      formKey: "stalpbr",
    },
    {
      label: "County",
      value: `${appState.filters.county}`,
      search: false,
      static: false,
      formKey: "county",
    },
  ];

  const locationsDataViz =
    dataView === "table" ? (
      <MultiHeaderTable
        tableBody={
          <BankFindLocationsStateList
            open={statesOpen}
            setOpen={setStatesOpen}
            countyData={countyRows}
            stateData={stateRows}
            total={totalOfficeCount}
            openCounties={openCounties}
            setOpenCounties={setOpenCounties}
          />
        }
        tableHeaders={LocationHeaders}
        sortProperties={{
          sortDirection: sort.sortOrder,
          sortField: sort.sortField,
          sortHandler,
        }}
        useStickyHeader
      />
    ) : (
      <USAMap
        setMode={setDataView}
        mapData={mapData}
        usaTotal={totalOfficeCount}
      />
    );

  const countDataViz =
    dataView === "table" ? (
      <Box
        sx={{
          "& td, & p": {
            fontSize: 16,
          },
        }}
      >
        <MultiHeaderTable
          tableHeaders={getCountHeaders(risdate)}
          tableBody={<BankFindLocationsCountTable data={countData} />}
        />
      </Box>
    ) : (
      <Box
        id="locations-chart"
        sx={{
          "& [class*='toolbar-master']": { display: "none" }, // hides the hamburger menu for image download
        }}
      >
        <FibFinancialsChart
          categories={chartCategories}
          dataset={chartData}
          onRender={(chart: any) => {
            chartInstanceRef.current = chart;
          }}
          chart={{
            numberPrefix: "",
            paletteColors: "#A48923,#929292,#6abcfb",
            xAxisName: "",
          }}
          chartType={"mscombi2d"}
          height="50%"
        />
      </Box>
    );

  const dataViz =
    report === "branch-office-locations" ? locationsDataViz : countDataViz;

  const locationsHeader =
    masterTotal !== totalOfficeCount
      ? `${totalOfficeCount} Branch Office${totalOfficeCount === 1 ? "" : "s"} of ${masterTotal}`
      : `${masterTotal} Branch Offices`;
  const countHeader = `${masterTotal} Branch Offices`;
  const header =
    report === "branch-office-locations" ? locationsHeader : countHeader;

  const locationsSubHeader =
    appState.filters.stalpbr === "" || !appState.filters.stalpbr
      ? "Branch Offices by State and County"
      : `Branch Offices of ${selectedStatesAlpha.filter((c) => c.value === appState.filters.stalpbr)[0]?.label} by County`;
  const countSubHeader =
    "Branch Office Count - Annual Timeseries Past Ten Years";
  const headerSubText =
    report === "branch-office-locations" ? locationsSubHeader : countSubHeader;
  return (
    <Root id="bankfindLocations" className={classes.locationPills}>
      <Typography
        color="primary.main"
        variant="h6"
        id="office-locations-count"
        sx={{
          marginBottom: 1,
          marginTop: 0,
        }}
      >
        {header}
      </Typography>
      {report === "branch-office-locations" ? (
        <ResultsViewToggle
          mode={dataView}
          onModeChange={(mode: string) => {
            setDataView(mode as "table" | "map");
          }}
          options={[
            { label: "table", icon: <TableChartIcon /> },
            { label: "map", icon: <PlaceOutlinedIcon /> },
          ]}
          dropDownOptions={dropDownOptions}
          dropDown={report}
          setDropDown={(selected: string) => {
            setReport(selected as Report);
          }}
          backToNational={appState.filters.stalpbr !== ""}
          onBackToNational={() => {
            updateFilter("stalpbr", "");
            updateFilter("county", "");
          }}
        />
      ) : (
        <ResultsViewToggle
          mode={dataView === "map" ? "chart" : "table"}
          onModeChange={(mode: string) => {
            const newMode = mode === "table" ? "table" : "map";
            setDataView(newMode);
          }}
          options={[
            { label: "table", icon: <TableChartIcon /> },
            { label: "chart", icon: <TimelineIcon /> },
          ]}
          dropDownOptions={dropDownOptions}
          dropDown={report}
          setDropDown={(selected: string) => {
            setReport(selected as Report);
          }}
        />
      )}
      {report === "branch-office-locations" && (
        <ShowHideButton
          title={""}
          body={<SearchPills dataArray={summaryTablesSearchPills} />}
        />
      )}
      <Typography
        color="primary.main"
        variant="h2"
        align="left"
        sx={{
          marginTop: report === "branch-office-count" ? 3 : 0,
          mb: report === "branch-office-count" ? -2 : 0,
        }}
      >
        {headerSubText}
      </Typography>
      {report === "branch-office-count" && (
        <Typography variant="subtitle1" color="primary.main" mb={1}>
          {Number(risdate.slice(0, 4)) - 9} - {risdate.slice(0, 4)}
        </Typography>
      )}
      {dataViz}
    </Root>
  );
}
